// @ts-check
// Configurações > Formulários de captação (contrato 08): lista, editor (campos, unidade, curso, origem, consultor,
// consentimento LGPD, Turnstile), endereços para divulgar ou embutir no site e as últimas submissões.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { CopyableValue } from './settings-channels-page.js';
import { AdminOnlyNote, CodeSnippet, FormError, SelectField, StatusBadge, TextField, describeError } from './integration-ui.js';
import {
  createCaptureForm, deleteCaptureForm, getCaptureForm, listCaptureForms, listCaptureSubmissions, updateCaptureForm,
} from '../../data/integrations.js';
import { api } from '../../data/api-client.js';
import { hasCurrentPermissionFlag, objectsByNameSingular } from '../../data/metadata-store.js';
import { loadWorkspaceMembers } from '../../data/workspace-members.js';
import { Link, navigate } from '../../app/router.js';
import { formatDateTime } from '../../lib/format.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').CaptureForm} CaptureForm
 * @typedef {import('../../data/integrations.js').CaptureFormSettings} CaptureFormSettings
 * @typedef {import('../../data/integrations.js').CaptureSubmission} CaptureSubmission
 */

// postcode: CEP da família (pacote 1.12.0), que o mapa de calor lê
const FIELD_KEYS = ['email', 'phone', 'postcode', 'students', 'studentBirthDate', 'studentGradeLevel', 'course', 'campus', 'message', 'marketingOptIn'];
// campos que só fazem sentido ligados ou desligados (sem obrigatoriedade)
const OPTIONAL_ONLY_FIELDS = new Set(['marketingOptIn']);
const MAX_STUDENT_OPTIONS = ['1', '2', '3', '4', '5', '6'];

// formulários são configuração da instituição (flag WORKSPACE do papel)
const isAdmin = () => hasCurrentPermissionFlag('WORKSPACE');

/** @param {string} namePlural */
async function loadOptions(namePlural) {
  try {
    const body = await api.get(`/rest/${namePlural}`, { limit: 200 });
    return (body?.data?.[namePlural] ?? []).map((/** @type {any} */ record) => ({ value: record.id, label: record.name ?? '' }));
  } catch {
    return [];
  }
}

function leadSourceOptions() {
  const field = objectsByNameSingular.value.get('person')?.fields.find((candidate) => candidate.name === 'leadSource');
  return (field?.options ?? []).map((option) => ({ value: option.value, label: translateMetadataLabel(option.label) }));
}

export function SettingsCaptureFormsPage() {
  const [forms, setForms] = useState(/** @type {CaptureForm[] | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  useEffect(() => {
    document.title = `${t('leadCapture.forms.title')} - ${t('navigation.settings')}`;
    listCaptureForms().then(setForms).catch((error) => {
      showErrorSnackbar(error);
      setForms([]);
    });
  }, []);
  const create = async () => {
    try {
      const form = await createCaptureForm({ name: name.trim() });
      navigate(`/settings/capture-forms/${form.id}`);
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconFileDescription" title=${t('leadCapture.forms.title')} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('leadCapture.forms.title')} description=${t('leadCapture.forms.description')}>
        ${isAdmin() ? html`<div class="integration-toolbar"><${Button} title=${t('leadCapture.forms.new')} icon="IconPlus" accent="blue" size="small" onClick=${() => setIsCreating(true)} /></div>` : null}
        ${forms === null
          ? html`<${Skeleton} height=${80} radius=${8} />`
          : forms.length === 0
            ? html`<div class="integration-empty">${t('leadCapture.forms.empty')}</div>`
            : html`<div class="settings-table integration-table">
                <div class="settings-table-row settings-table-header" data-columns="forms">
                  <span>${t('leadCapture.forms.name')}</span><span>${t('leadCapture.forms.submissions')}</span><span>${t('leadCapture.forms.lastSubmission')}</span>
                  <span>${t('leadCapture.common.status')}</span><span></span>
                </div>
                ${forms.map((form) => html`<${Link} key=${form.id} href=${`/settings/capture-forms/${form.id}`} className="settings-table-row" data-columns="forms">
                  <span class="settings-table-name"><${Icon} name="IconFileDescription" size=${16} />${form.name}</span>
                  <span class="settings-table-muted">${form.submissionCount}</span>
                  <span class="settings-table-muted">${form.lastSubmissionAt ? formatDateTime(form.lastSubmissionAt) : '-'}</span>
                  <span><${StatusBadge} status=${form.isActive ? 'ACTIVE' : 'INACTIVE'} label=${form.isActive ? t('leadCapture.forms.active') : t('leadCapture.forms.inactive')} /></span>
                  <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
                <//>`)}
              </div>`}
      <//>
      ${isCreating ? html`<${Modal} title=${t('leadCapture.forms.newTitle')} confirmLabel=${t('leadCapture.forms.create')} isConfirmDisabled=${!name.trim()}
        onClose=${() => setIsCreating(false)} onConfirm=${create}>
        <div class="integration-modal-body">
          <${TextField} label=${t('leadCapture.forms.name')} value=${name} onInput=${setName} placeholder=${t('leadCapture.forms.namePlaceholder')} isRequired=${true} autoFocus=${true} maxLength=${120} />
        </div>
      <//>` : null}
    </div>
  <//>`;
}

/**
 * @param {{ fields: CaptureFormSettings['fields'], onChange: (fields: CaptureFormSettings['fields']) => void }} props
 */
function FieldsEditor({ fields, onChange }) {
  return html`<div class="settings-table integration-table">
    <div class="settings-table-row settings-table-header" data-columns="form-fields">
      <span>${t('leadCapture.forms.field')}</span><span>${t('leadCapture.forms.showField')}</span><span>${t('leadCapture.forms.requiredField')}</span>
    </div>
    <div class="settings-table-row" data-columns="form-fields">
      <span class="settings-table-name">${t('leadCapture.forms.fields.guardianName')}</span>
      <span><${Checkbox} checked=${true} disabled=${true} ariaLabel=${t('leadCapture.forms.showField')} /></span>
      <span><${Checkbox} checked=${true} disabled=${true} ariaLabel=${t('leadCapture.forms.requiredField')} /></span>
    </div>
    ${FIELD_KEYS.map((key) => {
      const setting = fields[key] ?? { enabled: false, required: false };
      return html`<div key=${key} class="settings-table-row" data-columns="form-fields">
        <span class="settings-table-name">${t(`leadCapture.forms.fields.${key}`)}</span>
        <span><${Checkbox} checked=${setting.enabled} ariaLabel=${t('leadCapture.forms.showField')}
          onChange=${(/** @type {boolean} */ checked) => onChange({ ...fields, [key]: { enabled: checked, required: checked && setting.required } })} /></span>
        <span>${OPTIONAL_ONLY_FIELDS.has(key) ? null : html`<${Checkbox} checked=${setting.required} disabled=${!setting.enabled} ariaLabel=${t('leadCapture.forms.requiredField')}
          onChange=${(/** @type {boolean} */ checked) => onChange({ ...fields, [key]: { enabled: setting.enabled, required: checked } })} />`}</span>
      </div>`;
    })}
  </div>
  ${fields.marketingOptIn?.enabled
    ? html`<p class="marketing-opt-in-hint" data-testid="marketing-opt-in-hint"><${Icon} name="IconInfoCircle" size=${14} /><span>${t('leadCapture.forms.marketingOptInFunnelHint')}</span></p>`
    : null}`;
}

/**
 * @param {{ submissions: CaptureSubmission[] | null }} props
 */
function SubmissionList({ submissions }) {
  if (submissions === null) return html`<${Skeleton} height=${60} radius=${8} />`;
  if (submissions.length === 0) return html`<div class="integration-empty">${t('leadCapture.forms.noSubmissions')}</div>`;
  return html`<div class="settings-table integration-table">
    <div class="settings-table-row settings-table-header" data-columns="submissions">
      <span>${t('leadCapture.webhooks.when')}</span><span>${t('leadCapture.forms.guardian')}</span><span>${t('leadCapture.forms.origin')}</span><span>${t('leadCapture.forms.result')}</span>
    </div>
    ${submissions.map((submission) => {
      const payload = submission.payload ?? {};
      const guardianName = payload.guardianName ?? [payload.guardian?.firstName, payload.guardian?.lastName].filter(Boolean).join(' ');
      return html`<div key=${submission.id} class="settings-table-row" data-columns="submissions">
        <span class="settings-table-muted">${formatDateTime(submission.createdAt)}</span>
        <span class="settings-table-name">${submission.enrollmentOpportunityId
          ? html`<${Link} href=${`/object/enrollmentOpportunity/${submission.enrollmentOpportunityId}`} className="integration-link">${guardianName || t('leadCapture.forms.openCard')}<//>`
          : guardianName}</span>
        <span class="settings-table-muted">${t(`leadCapture.forms.origins.${submission.origin}`)}</span>
        <span class="settings-table-muted">${submission.isExistingPerson ? t('leadCapture.forms.existingFamily') : t('leadCapture.forms.newFamily')}</span>
      </div>`;
    })}
  </div>`;
}

/**
 * @param {{ formId: string }} props
 */
export function SettingsCaptureFormDetailPage({ formId }) {
  const [form, setForm] = useState(/** @type {CaptureForm | null} */ (null));
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [settings, setSettings] = useState(/** @type {CaptureFormSettings | null} */ (null));
  const [options, setOptions] = useState(/** @type {{ campuses: any[], courses: any[], campaigns: any[], members: any[] }} */ ({ campuses: [], courses: [], campaigns: [], members: [] }));
  const [submissions, setSubmissions] = useState(/** @type {CaptureSubmission[] | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /** @param {CaptureForm} loaded */
  const applyForm = (loaded) => {
    setForm(loaded);
    setName(loaded.name);
    setIsActive(loaded.isActive);
    setSettings(loaded.settings);
  };
  useEffect(() => {
    document.title = `${t('leadCapture.forms.title')} - ${t('navigation.settings')}`;
    getCaptureForm(formId).then(applyForm).catch((requestError) => {
      showErrorSnackbar(requestError);
      navigate('/settings/capture-forms', { replace: true });
    });
    listCaptureSubmissions(formId).then(setSubmissions).catch(() => setSubmissions([]));
    Promise.all([loadOptions('campuses'), loadOptions('courses'), loadOptions('campaigns'), loadWorkspaceMembers()]).then(([campuses, courses, campaigns, members]) => {
      setOptions({
        campuses, courses, campaigns,
        members: members.map((member) => ({ value: member.id, label: formatFullName(member.name) || member.userEmail || '' })),
      });
    });
  }, [formId]);

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/capture-forms" className="record-breadcrumb-prefix">${t('leadCapture.forms.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${form?.name ?? ''}</span>
  </nav>`;
  if (!form || !settings) return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}><div class="settings-page"><${Skeleton} height=${240} radius=${8} /></div><//>`;

  const canEdit = isAdmin();
  /** @param {Partial<CaptureFormSettings>} patch */
  const change = (patch) => setSettings({ ...settings, ...patch });
  /** @param {string} value */
  const nullable = (value) => (value === '' ? null : value);
  const withNone = (/** @type {Array<{ value: string, label: string }>} */ list, /** @type {string} */ noneLabel) => [{ value: '', label: noneLabel }, ...list];

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      applyForm(await updateCaptureForm(formId, { name: name.trim(), isActive, settings }));
      showSuccessSnackbar(t('leadCapture.common.saved'));
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${html`<${Button} title=${t('leadCapture.forms.open')} icon="IconExternalLink" variant="secondary" size="small"
      onClick=${() => window.open(form.publicUrl, '_blank', 'noopener')} />`} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('leadCapture.forms.shareTitle')} description=${t('leadCapture.forms.shareDescription')}>
        <div class="form-field"><span class="form-field-label">${t('leadCapture.forms.publicUrl')}</span><${CopyableValue} value=${form.publicUrl} label=${t('leadCapture.common.copy')} /></div>
        <${CodeSnippet} label=${t('leadCapture.forms.embedScript')} code=${form.embedSnippet} />
        <${CodeSnippet} label=${t('leadCapture.forms.embedIframe')} code=${form.iframeSnippet} />
        <p class="input-hint">${t('leadCapture.forms.utmHint')}</p>
      <//>
      ${!canEdit ? html`<${AdminOnlyNote} />` : null}
      <fieldset class="integration-fieldset" disabled=${!canEdit}>
        <${SettingsSection} title=${t('leadCapture.forms.contentTitle')} description=${t('leadCapture.forms.contentDescription')}>
          <div class="channel-form">
            <div class="form-field-row">
              <${TextField} label=${t('leadCapture.forms.name')} value=${name} onInput=${setName} isRequired=${true} maxLength=${120} />
              <${TextField} label=${t('leadCapture.forms.pageTitle')} value=${settings.title} onInput=${(value) => change({ title: value })} maxLength=${160} />
            </div>
            <${TextField} label=${t('leadCapture.forms.introText')} value=${settings.description} onInput=${(value) => change({ description: value })} multiline=${true} maxLength=${1000} />
            <div class="form-field-row">
              <${TextField} label=${t('leadCapture.forms.submitLabel')} value=${settings.submitLabel} onInput=${(value) => change({ submitLabel: value })} maxLength=${40} />
              <${SelectField} label=${t('leadCapture.forms.maxStudents')} value=${String(settings.maxStudents)} onChange=${(value) => change({ maxStudents: Number(value) })}
                options=${MAX_STUDENT_OPTIONS.map((value) => ({ value, label: value }))} />
            </div>
            <${TextField} label=${t('leadCapture.forms.successMessage')} value=${settings.successMessage} onInput=${(value) => change({ successMessage: value })} multiline=${true} maxLength=${500} />
            <${TextField} label=${t('leadCapture.forms.redirectUrl')} value=${settings.redirectUrl ?? ''} onInput=${(value) => change({ redirectUrl: nullable(value.trim()) })}
              placeholder="https://escola.com.br/obrigado" hint=${t('leadCapture.forms.redirectHint')} type="url" />
          </div>
        <//>
        <${SettingsSection} title=${t('leadCapture.forms.fieldsTitle')} description=${t('leadCapture.forms.fieldsDescription')}>
          <${FieldsEditor} fields=${settings.fields} onChange=${(fields) => change({ fields })} />
        <//>
        <${SettingsSection} title=${t('leadCapture.forms.routingTitle')} description=${t('leadCapture.forms.routingDescription')}>
          <div class="channel-form">
            <div class="form-field-row">
              <${SelectField} label=${t('leadCapture.forms.campus')} value=${settings.campusId ?? ''} onChange=${(value) => change({ campusId: nullable(value) })}
                options=${withNone(options.campuses, t('leadCapture.forms.noDefault'))} hint=${t('leadCapture.forms.campusHint')} />
              <${SelectField} label=${t('leadCapture.forms.course')} value=${settings.courseId ?? ''} onChange=${(value) => change({ courseId: nullable(value) })}
                options=${withNone(options.courses, t('leadCapture.forms.noDefault'))} hint=${t('leadCapture.forms.courseHint')} />
            </div>
            <div class="form-field-row">
              <${SelectField} label=${t('leadCapture.forms.leadSource')} value=${settings.leadSource ?? ''} onChange=${(value) => change({ leadSource: nullable(value) })}
                options=${withNone(leadSourceOptions(), t('leadCapture.forms.noDefault'))} />
              <${SelectField} label=${t('leadCapture.forms.campaign')} value=${settings.campaignId ?? ''} onChange=${(value) => change({ campaignId: nullable(value) })}
                options=${withNone(options.campaigns, t('leadCapture.forms.noDefault'))} />
            </div>
            <${SelectField} label=${t('leadCapture.forms.owner')} value=${settings.ownerWorkspaceMemberId ?? ''} onChange=${(value) => change({ ownerWorkspaceMemberId: nullable(value) })}
              options=${withNone(options.members, t('leadCapture.forms.noOwner'))} hint=${t('leadCapture.forms.ownerHint')} />
          </div>
        <//>
        <${SettingsSection} title=${t('leadCapture.forms.privacyTitle')} description=${t('leadCapture.forms.privacyDescription')}>
          <div class="channel-form">
            <${TextField} label=${t('leadCapture.forms.consentText')} value=${settings.consentText} onInput=${(value) => change({ consentText: value })} multiline=${true} maxLength=${1500}
              hint=${t('leadCapture.forms.consentHint')} />
            <div class="form-field-row">
              <${TextField} label=${t('leadCapture.forms.privacyNoticeUrl')} value=${settings.privacyNoticeUrl ?? ''} onInput=${(value) => change({ privacyNoticeUrl: nullable(value.trim()) })}
                placeholder="https://escola.com.br/privacidade" type="url" />
              <${TextField} label=${t('leadCapture.forms.privacyNoticeVersion')} value=${settings.privacyNoticeVersion} onInput=${(value) => change({ privacyNoticeVersion: value })} maxLength=${40} />
            </div>
            <label class="integration-toggle-row">
              <${Toggle} value=${settings.requireTurnstile} onChange=${(value) => change({ requireTurnstile: value })} ariaLabel=${t('leadCapture.forms.turnstile')} />
              <span>${t('leadCapture.forms.turnstile')}<span class="input-hint integration-block-hint">${t('leadCapture.forms.turnstileHint')}</span></span>
            </label>
            <label class="integration-toggle-row">
              <${Toggle} value=${isActive} onChange=${setIsActive} ariaLabel=${t('leadCapture.forms.active')} />
              <span>${t('leadCapture.forms.activeLabel')}</span>
            </label>
            <${FormError} message=${error} />
            ${canEdit ? html`<div class="channel-form-actions">
              <${Button} title=${t('leadCapture.common.save')} icon="IconCheck" accent="blue" size="small" isLoading=${isSaving} disabled=${!name.trim()} onClick=${save} />
            </div>` : null}
          </div>
        <//>
      </fieldset>
      <${SettingsSection} title=${t('leadCapture.forms.recentSubmissions')} description=${t('leadCapture.forms.recentSubmissionsDescription')}>
        <${SubmissionList} submissions=${submissions} />
      <//>
      ${canEdit
        ? html`<${SettingsSection} title=${t('leadCapture.common.dangerZone')}>
            <div><${Button} title=${t('leadCapture.forms.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleting(true)} /></div>
          <//>`
        : null}
      ${isDeleting ? html`<${Modal} title=${t('leadCapture.forms.deleteTitle')} subtitle=${t('leadCapture.forms.deleteSubtitle')} confirmLabel=${t('leadCapture.forms.delete')}
        confirmAccent="danger" onClose=${() => setIsDeleting(false)} onConfirm=${async () => {
          try {
            await deleteCaptureForm(formId);
            showSuccessSnackbar(t('leadCapture.forms.deletedSnackbar'));
            navigate('/settings/capture-forms', { replace: true });
          } catch (requestError) {
            showErrorSnackbar(requestError);
            setIsDeleting(false);
          }
        }} />` : null}
    </div>
  <//>`;
}
